import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

import { Field, type FieldProps } from './Field';

export interface DebouncedFieldProps extends Omit<FieldProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
}

export const DebouncedField = ({
  value,
  onChange,
  debounceMs = 300,
  ...props
}: DebouncedFieldProps) => {
  const [localValue, setLocalValue] = useState(value);

  const debouncedOnChange = useMemo(
    () => debounce((val: string) => onChange(val), debounceMs),
    [onChange, debounceMs],
  );

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Update local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle immediate local state updates
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  return (
    <Field
      {...props}
      value={localValue}
      onBlur={() => debouncedOnChange.flush()}
      onChange={handleChange}
    />
  );
};
